import ConfirmationNumber from '@mui/icons-material/ConfirmationNumber';
import MyLocation from '@mui/icons-material/MyLocation';
import Smartphone from '@mui/icons-material/Smartphone';
import SmartToy from '@mui/icons-material/SmartToy';
import Webhook from '@mui/icons-material/Webhook';
import type { Theme } from '@mui/material';
import Box from '@mui/material/Box';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import { JobEventSource } from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';

import { type JobEventSimulation } from './JobEventSimulation';

export const COLUMN_COUNT = 4;

const EVENT_SOURCE_ICONS: Record<string, React.ReactNode> = {
  [JobEventSource.WEB]: <Smartphone fontSize="inherit" />,
  [JobEventSource.WEBHOOK]: <Webhook fontSize="inherit" />,
  [JobEventSource.AUTOMATION]: <SmartToy fontSize="inherit" />,
  [JobEventSource.TICKET_MATCH]: <ConfirmationNumber fontSize="inherit" />,
  [JobEventSource.GEOFENCE]: <MyLocation fontSize="inherit" />,
};

const headerCellSx = {
  fontWeight: 600,
  backgroundColor: (theme: Theme) => theme.semantic.surfaceSunken,
  borderBottom: (theme: Theme) => `2px solid ${theme.palette.divider}`,
};

export const tableSx = {
  border: (theme: Theme) => `1px solid ${theme.palette.divider}`,
  borderRadius: 1,
  fontFamily: 'monospace',
  '& .MuiTableCell-root': {
    fontFamily: 'inherit',
    paddingTop: 1,
    paddingBottom: 1,
    lineHeight: 1.2,
    fontSize: '0.8125rem',
    whiteSpace: 'nowrap',
  },
  '& .MuiTableRow-root': {
    '& td, & th': { borderBottomWidth: 1 },
  },
};

export const titleCellSx = {
  fontWeight: 600,
  backgroundColor: (theme: Theme) => theme.semantic.surfaceSunken,
  borderBottom: (theme: Theme) => `2px solid ${theme.palette.divider}`,
};

const emDashCellSx = { color: 'text.secondary', fontStyle: 'italic' };

export const stripedRowSx = (index: number) => ({
  backgroundColor: (theme: Theme) =>
    index % 2 === 1 ? theme.semantic.surfaceSunken : 'transparent',
});

export function EventCells({ event }: { event: JobEventSimulation }) {
  return (
    <>
      <TableCell>
        {dayjs.tz(event.occurredAt).format('MMM DD, YYYY HH:mm:ss Z')}
      </TableCell>
      <TableCell>{event.eventName}</TableCell>
      <TableCell>{event.loadId ?? '—'}</TableCell>
      <TableCell>
        <Tooltip title={event.eventSource} placement="top">
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              verticalAlign: 'middle',
              lineHeight: 1,
              // Icons inherit the cell's 0.8125rem font, which is too small for
              // the glyphs to read; 1rem keeps them roughly text height.
              '& .MuiSvgIcon-root': { fontSize: '1rem' },
            }}
          >
            {EVENT_SOURCE_ICONS[event.eventSource] ?? event.eventSource}
          </Box>
        </Tooltip>
      </TableCell>
    </>
  );
}

export function EmptyRowCells() {
  return (
    <>
      <TableCell sx={emDashCellSx}>—</TableCell>
      <TableCell sx={emDashCellSx}>—</TableCell>
      <TableCell sx={emDashCellSx}>—</TableCell>
      <TableCell sx={emDashCellSx}>—</TableCell>
    </>
  );
}

export function HeaderRow() {
  return (
    <TableRow>
      <TableCell sx={headerCellSx}>{t('admin.job_geo_debug.column_time')}</TableCell>
      <TableCell sx={headerCellSx}>
        {t('admin.job_geo_debug.column_event_name')}
      </TableCell>
      <TableCell sx={headerCellSx}>{t('admin.job_geo_debug.column_load_id')}</TableCell>
      <TableCell sx={headerCellSx}>
        {t('admin.job_geo_debug.column_event_source')}
      </TableCell>
    </TableRow>
  );
}
